import { useCallback, useContext, useState } from 'react';
import dayjs from 'dayjs';
import { QueryClientContext } from '@tanstack/react-query';

import {
  buildVmtRecordFromSelection,
  getProgramNameFromRecord,
  sanitizeRecords,
  validateVmtRecord,
} from '../../utils/vmt';
import { buildQuickActionPatch } from '../../utils/vmtQuickActions';
import { formatVmtSendErrorMessage } from '../../utils/vmtSendError';
import { buildEnrollmentInfoFields, resolveSelectionForStaging } from '../../utils/productSelectionsRow';

const useProductSelectionsActions = ({
  selectedRows,
  buildVmtContextFromRow,
  stageRecord,
  stageRecords,
  sendMutation,
  userEmail,
  onNotify,
  setSnackbar,
}) => {
  const queryClient = useContext(QueryClientContext);

  const [sendMenu, setSendMenu] = useState({ anchorEl: null, row: null });
  const [notesModal, setNotesModal] = useState({ open: false, row: null, notes: '' });
  const [quickActionDialog, setQuickActionDialog] = useState({ type: null, row: null });
  const [quickActionReferenceId, setQuickActionReferenceId] = useState('');
  const [quickActionDate, setQuickActionDate] = useState(dayjs());

  const handleSendToStaging = useCallback(
    (row, event, notes = '') => {
      if (event) event.stopPropagation();
      const selection = resolveSelectionForStaging(row);
      if (!selection) return;
      const record = buildVmtRecordFromSelection(selection, buildVmtContextFromRow(row));
      const trimmedNote = notes?.trim();
      if (trimmedNote) record.notes = trimmedNote;
      stageRecord(record, buildEnrollmentInfoFields(row));
      setSnackbar({
        open: true,
        message: `Selection added to bulk-send staging${trimmedNote ? ' with note' : ''}.`,
        severity: 'success',
      });
    },
    [buildVmtContextFromRow, setSnackbar, stageRecord]
  );

  const handleSendMenuOpen = useCallback((event, row) => {
    event.preventDefault();
    event.stopPropagation();
    setSendMenu({ anchorEl: event.currentTarget, row });
  }, []);

  const handleSendMenuClose = useCallback(() => setSendMenu({ anchorEl: null, row: null }), []);

  const handleMenuSendDirect = useCallback(
    (row) => {
      if (row) handleSendToStaging(row);
      handleSendMenuClose();
    },
    [handleSendMenuClose, handleSendToStaging]
  );

  const handleMenuSendWithNotes = useCallback(
    (row) => {
      handleSendMenuClose();
      if (!row) return;
      setNotesModal({ open: true, row, notes: '' });
    },
    [handleSendMenuClose]
  );

  const closeQuickActionDialog = useCallback(() => {
    setQuickActionDialog({ type: null, row: null });
    setQuickActionReferenceId('');
  }, []);

  const openPopcornPrompt = useCallback((row) => {
    if (!row) return;
    setQuickActionReferenceId('');
    setQuickActionDialog({ type: 'popcorn', row });
  }, []);

  const openSetDatesPrompt = useCallback((row) => {
    if (!row) return;
    const candidate = row?.selectionDate || row?.effectiveDate || row?.billingDate || null;
    const parsed = candidate ? dayjs(candidate, 'MM/DD/YY') : null;
    setQuickActionDate(parsed && parsed.isValid() ? parsed : dayjs());
    setQuickActionDialog({ type: 'setDates', row });
  }, []);

  const sendQuickActionToVmt = useCallback(
    async ({ row, actionKey, options = {} }) => {
      if (!row) return;
      const selection = resolveSelectionForStaging(row);
      if (!selection) {
        setSnackbar({ open: true, message: 'Selection data is unavailable for this row.', severity: 'warning' });
        return;
      }
      const baseRecord = buildVmtRecordFromSelection(selection, buildVmtContextFromRow(row));
      const patch = buildQuickActionPatch(actionKey, {
        bac: selection?.bac || row?.bac,
        productCd: baseRecord?.productCd || row?.productCd,
        ...options,
      });
      if (!patch || Object.keys(patch).length === 0) {
        setSnackbar({ open: true, message: 'No updates were applied for this action.', severity: 'info' });
        return;
      }
      const mergedRecord = { ...baseRecord, ...patch };
      const sanitized = sanitizeRecords([mergedRecord])[0];
      const validation = validateVmtRecord(sanitized);
      if (Object.keys(validation).length > 0) {
        const missing = Object.keys(validation).join(', ');
        setSnackbar({ open: true, message: `Missing required fields: ${missing}`, severity: 'error' });
        return;
      }
      try {
        await sendMutation.mutateAsync({ records: [sanitized], userEmail: userEmail || null });
        setSnackbar({ open: true, message: 'Selection sent to VMT.', severity: 'success' });
        const programName = getProgramNameFromRecord(sanitized) || sanitized.programName || sanitized.programLabel || '';
        onNotify?.('Selection sent to VMT.', 'success', {
          refreshDealer: true,
          refreshProgramName: programName,
          refreshBac: sanitized.bac,
          refreshDelayMs: 1500,
          navigateToHistory: true,
          suppressSnackbar: true,
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: formatVmtSendErrorMessage(error),
          severity: 'error',
        });
      } finally {
        if (queryClient) {
          try {
            await queryClient.invalidateQueries({ queryKey: ['vmtSendHistory'] });
          } catch (_err) {
            // noop
          }
        }
      }
    },
    [buildVmtContextFromRow, onNotify, queryClient, sendMutation, setSnackbar, userEmail]
  );

  const handleMenuQuickAction = useCallback(
    (row, actionKey) => {
      handleSendMenuClose();
      if (!row) return;
      void sendQuickActionToVmt({ row, actionKey });
    },
    [handleSendMenuClose, sendQuickActionToVmt]
  );

  const handleOpenPopcornPrompt = useCallback(
    (row) => {
      handleSendMenuClose();
      openPopcornPrompt(row);
    },
    [handleSendMenuClose, openPopcornPrompt]
  );

  const handleOpenSetDatesPrompt = useCallback(
    (row) => {
      handleSendMenuClose();
      openSetDatesPrompt(row);
    },
    [handleSendMenuClose, openSetDatesPrompt]
  );

  const handleNotesChange = useCallback((value) => {
    setNotesModal((prev) => ({ ...prev, notes: value }));
  }, []);

  const handleNotesCancel = useCallback(() => {
    setNotesModal({ open: false, row: null, notes: '' });
  }, []);

  const handleNotesSubmit = useCallback(() => {
    if (!notesModal.row) return;
    handleSendToStaging(notesModal.row, null, notesModal.notes);
    setNotesModal({ open: false, row: null, notes: '' });
  }, [handleSendToStaging, notesModal.notes, notesModal.row]);

  const handleQuickActionReferenceIdChange = useCallback((value) => {
    setQuickActionReferenceId(value);
  }, []);

  const handleQuickActionDateChange = useCallback((value) => {
    setQuickActionDate(value);
  }, []);

  const handleSubmitPopcorn = useCallback(() => {
    const target = quickActionDialog.row;
    const trimmed = quickActionReferenceId.trim();
    if (!trimmed) return;
    closeQuickActionDialog();
    void sendQuickActionToVmt({
      row: target,
      actionKey: 'forcePopcornParentLive',
      options: { referenceId: trimmed },
    });
  }, [closeQuickActionDialog, quickActionDialog.row, quickActionReferenceId, sendQuickActionToVmt]);

  const handleSubmitSetDates = useCallback(() => {
    const target = quickActionDialog.row;
    if (!quickActionDate || !quickActionDate.isValid || !quickActionDate.isValid()) return;
    closeQuickActionDialog();
    void sendQuickActionToVmt({
      row: target,
      actionKey: 'setDates',
      options: { dateValue: quickActionDate },
    });
  }, [closeQuickActionDialog, quickActionDate, quickActionDialog.row, sendQuickActionToVmt]);

  const handleBulkStageSelected = useCallback(() => {
    if (!selectedRows.length) {
      setSnackbar({ open: true, message: 'Select at least one row to stage.', severity: 'info' });
      return;
    }
    const payload = selectedRows.map((row) => {
      const selection = resolveSelectionForStaging(row);
      return {
        record: buildVmtRecordFromSelection(selection, buildVmtContextFromRow(row)),
        infoFields: buildEnrollmentInfoFields(row),
      };
    });
    stageRecords(payload);
    setSnackbar({ open: true, message: `Staged ${payload.length} selection(s).`, severity: 'success' });
  }, [buildVmtContextFromRow, selectedRows, setSnackbar, stageRecords]);

  return {
    sendMenu,
    notesModal,
    quickActionDialog,
    quickActionReferenceId,
    quickActionDate,
    handleSendToStaging,
    handleSendMenuOpen,
    handleSendMenuClose,
    handleMenuSendDirect,
    handleMenuSendWithNotes,
    handleMenuQuickAction,
    handleOpenPopcornPrompt,
    handleOpenSetDatesPrompt,
    handleNotesChange,
    handleNotesCancel,
    handleNotesSubmit,
    handleQuickActionReferenceIdChange,
    handleQuickActionDateChange,
    closeQuickActionDialog,
    handleSubmitPopcorn,
    handleSubmitSetDates,
    handleBulkStageSelected,
  };
};

export default useProductSelectionsActions;
